import { useState } from "react";
import useHttp from "./use_http";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import {
  forgotPasswordSchema,
  loginSchema,
  resetPasswordSchema,
  signupSchema,
  verifySignupSchema,
} from "../utils/schema";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setLogoutLoading,
  setLogoutModal,
  setUserProfile,
  setUserProfileLoading,
} from "../features/app/app_slice";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const useAuth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { httpService } = useHttp();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [loadings, setLoadings] = useState({
    getCategories: false,
    signup: false,
    verifySignup: false,
    checkEmail: false,
    login: false,
    forgotPasswordCheckEmail: false,
    forgotPassword: false,
    resetPassword: false,
    resendVerificationCode: false,
    loginFromAdmin: false,
    handleGetCaptcha: false,
    forgotPasswordInstructorCheck: false,
  });
  const [getCountriesLoading, setGetCountriesLoading] = useState(false);

  const [step, setStep] = useState(1);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [now, setNow] = useState(null);
  const [showReset, setShowReset] = useState(true);

  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);

  const getUserProfile = async () => {
    try {
      dispatch(setUserProfileLoading(true));
      const response = await httpService.get("user/profile");
      dispatch(setUserProfileLoading(false));
      if (response.data?.userRoles?.find((o) => o.role.name == "admin")) {
        toast.error("This email belongs to an admin account.");
        dispatch(setUserProfile(null));
        dispatch(setLogoutModal(false));
        localStorage.removeItem("token");
        navigate("/login");
      } else if (
        !response.data?.userRoles?.find((o) => o.role.name == "instructor")
      ) {
        toast.error("This email doesn't belongs to an instructor account.");
        dispatch(setUserProfile(null));
        dispatch(setLogoutModal(false));
        localStorage.removeItem("token");
        navigate("/login");
      } else {
        dispatch(setUserProfile(response.data));
      }
    } catch ({ err, response }) {
      dispatch(setUserProfileLoading(false));
    }
  };

  const resendVerificationCode = async (email, status) => {
    let postData = {
      email: email,
      status: status,
    };
    try {
      setLoadings({ ...loadings, resendVerificationCode: true });
      const response = await httpService.post("auth/token/resend", postData);
      setLoadings({ ...loadings, resendVerificationCode: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setShowReset(false);
        setNow(Date.now() + 120000);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, resendVerificationCode: false });
    }
  };

  const getCountries = async () => {
    try {
      setGetCountriesLoading(true);
      const response = await httpService.get("country");
      setGetCountriesLoading(false);
      setCountries(response.data);
    } catch ({ err, response }) {
      setGetCountriesLoading(false);
    }
  };

  const getCategories = async () => {
    let array = [];
    try {
      setLoadings({ ...loadings, getCategories: true });
      const response = await httpService.get("category");
      setLoadings({ ...loadings, getCategories: false });
      response.data.map((item) => {
        array.push({
          label: item.translations?.en?.name,
          value: item.id,
        });
      });

      setCategories(array);
    } catch ({ err, response }) {
      setLoadings({ ...loadings, getCategories: false });
    }
  };

  const signupController = useFormik({
    initialValues: {
      fullname: "",
      countryId: null,
      categoryIds: [],
      bio: "",
      mobile: "",
      email: "",
      gender: null,
      address: "",
      acceptedTerms: false,
    },
    validationSchema: signupSchema,
    onSubmit: (values) => {
      signup(values);
    },
  });

  const signup = async (values) => {
    let postData = {
      fullname: values.fullname,
      countryId: values.countryId,
      categoryIds: values.categoryIds,
      bio: values.bio,
      mobile: values.mobile,
      email: values.email,
      gender: values.gender,
      address: values.address,
    };
    try {
      setLoadings({ ...loadings, signup: true });
      const response = await httpService.post(
        "instructor/application",
        postData,
      );
      setLoadings({ ...loadings, signup: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        verifySignupController.setFieldValue("email", values.email);
        setShowReset(false);
        setNow(Date.now() + 120000);
        setStep(2);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, signup: false });
    }
  };

  const verifySignupController = useFormik({
    initialValues: {
      email: "",
      token: "",
    },
    validationSchema: verifySignupSchema,
    onSubmit: (values) => {
      verifySignup(values);
    },
  });

  const verifySignup = async (values) => {
    let postData = {
      email: values.email,
      token: values.token,
    };
    try {
      setLoadings({ ...loadings, verifySignup: true });
      const response = await httpService.post(
        "instructor/application/verify",
        postData,
      );
      setLoadings({ ...loadings, verifySignup: false });
      if (response.status < 400) {
        setStep(3);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, verifySignup: false });
    }
  };

  const checkEmail = async (values, recaptchaToken) => {
    try {
      setLoadings({ ...loadings, checkEmail: true });
      const response = await httpService.get(`auth/check/${values.email}`);
      setLoadings({ ...loadings, checkEmail: false });
      if (response.status < 400) {
        if (response.data.staus) {
          login(values, recaptchaToken);
        } else if (response.data.status == 0) {
          toast.error(
            "No instructor account was found with this email. Please create an account first.",
          );
        }
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, checkEmail: false });
    }
  };

  const handleGetCaptcha = async (values) => {
    if (!executeRecaptcha) {
      toast.error("reCAPTCHA not ready");
      return;
    }
    setLoadings({ ...loadings, handleGetCaptcha: true });
    const recaptchaToken = await executeRecaptcha("login");
    setLoadings({ ...loadings, handleGetCaptcha: false });

    checkEmail(values, recaptchaToken);
  };

  const loginController = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: (values) => {
      handleGetCaptcha(values);
    },
  });

  const login = async (values, recaptchaToken) => {
    let postData = {
      email: values.email,
      password: values.password,
      captchaToken: recaptchaToken,
    };
    try {
      setLoadings({ ...loadings, login: true });
      const response = await httpService.post("auth/signin", postData);
      setLoadings({ ...loadings, login: false });
      if (response.status < 400) {
        if (
          response.data.user?.userRoles?.find((o) => o.role.name == "admin")
        ) {
          toast.error(
            "This email belongs to an admin account. You cannot log in to the instructors panel.",
          );
        } else if (
          !response.data.user?.userRoles?.find(
            (o) => o.role.name == "instructor",
          )
        ) {
          toast.error(
            "This email doesn't belongs to an instructor account. You cannot log in to the instructors panel.",
          );
        } else {
          toast.success(response.data.message);
          navigate("/dashboard");
          localStorage.setItem("token", response.data.token);
          dispatch(setUserProfile(response.data.user));
        }
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, login: false });
    }
  };

  const logout = async () => {
    try {
      dispatch(setLogoutLoading(true));
      const response = await httpService.get("auth/logout");
      dispatch(setLogoutLoading(false));
      if (response.status < 400) {
        toast.success(response.data.message);
        dispatch(setUserProfile(null));
        dispatch(setLogoutModal(false));
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch ({ err, response }) {
      dispatch(setLogoutLoading(false));
    }
  };

  const forgotPasswordController = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: (values) => {
      forgotPasswordCheckEmail(values);
    },
  });

  const forgotPasswordCheckEmail = async (values) => {
    try {
      setLoadings({ ...loadings, forgotPasswordCheckEmail: true });
      const response = await httpService.get(`auth/check/${values.email}`);
      setLoadings({ ...loadings, forgotPasswordCheckEmail: false });
      if (response.data.staus) {
        forgotPasswordInstructorCheck(values);
      } else {
        toast.error("No account was found with this email.");
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, forgotPasswordCheckEmail: false });
    }
  };

  const forgotPasswordInstructorCheck = async (values) => {
    try {
      setLoadings({ ...loadings, forgotPasswordInstructorCheck: true });
      const response = await httpService.get(
        `instructor/check/${values.email}`,
      );
      setLoadings({ ...loadings, forgotPasswordInstructorCheck: false });
      if (response.data.status) {
        forgotPassword(values);
      } else {
        toast.error(
          "This email doesn't belongs to an instructor account. You cannot reset password for this account.",
        );
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, forgotPasswordInstructorCheck: false });
    }
  };

  const forgotPassword = async (values) => {
    let postData = {
      email: values.email,
      deliveryMethod: "email",
    };
    try {
      setLoadings({ ...loadings, forgotPassword: true });
      const response = await httpService.post("auth/forgot-password", postData);
      setLoadings({ ...loadings, forgotPassword: false });
      if (response.status < 400) {
        resetPasswordController.setFieldValue("email", values.email);
        resetPasswordController.setFieldValue("code", "");
        resetPasswordController.setFieldValue("password", "");
        resetPasswordController.setFieldValue("password_confirm", "");
        setShowReset(false);
        setNow(Date.now() + 120000);
        setForgotPasswordStep(2);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, forgotPassword: false });
    }
  };

  const resetPasswordController = useFormik({
    initialValues: {
      email: "",
      code: "",
      password: "",
      password_confirm: "",
    },
    validationSchema: resetPasswordSchema,
    onSubmit: (values) => {
      if (values.password !== values.password_confirm) {
        toast.error("Password and password confirmation is not equal.");
      } else {
        resetPassword(values);
      }
    },
  });

  const resetPassword = async (values) => {
    let postData = {
      email: values.email,
      code: values.code,
      newPassword: values.password_confirm,
    };
    try {
      setLoadings({ ...loadings, resetPassword: true });
      const response = await httpService.post("auth/reset-password", postData);
      setLoadings({ ...loadings, resetPassword: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        navigate("/login");
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, resetPassword: false });
    }
  };

  const loginFromAdmin = async (token) => {
    try {
      setLoadings({ ...loadings, loginFromAdmin: true });
      const response = await httpService.get(
        `instructor/admin/exchange/${token}`,
      );
      setLoadings({ ...loadings, loginFromAdmin: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        navigate("/dashboard");
        localStorage.setItem("token", response.data.token);
        dispatch(setUserProfile(response.data.user));
      } else {
        navigate("/login");
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, loginFromAdmin: false });
      navigate("/login");
    }
  };

  const exports = {
    // entity methods
    getUserProfile,
    resendVerificationCode,
    getCountries,
    getCategories,
    logout,
    loginFromAdmin,
    // controller methods
    signupController,
    verifySignupController,
    loginController,
    forgotPasswordController,
    resetPasswordController,
    // entity states
    countries,
    categories,
    // controller states
    step,
    setStep,
    forgotPasswordStep,
    setForgotPasswordStep,
    now,
    setNow,
    showReset,
    setShowReset,
    // loading states
    loadings,
    getCountriesLoading,
  };
  return exports;
};
export default useAuth;
